/** 조회 조건 — 머리표·상세표가 같은 조건을 공유한다(BE 가 같은 where 절을 쓴다). 지정일자(assignDate)만 상세표 전용이다. */
export interface ReturnReceiptFilter {
  /**
   * 센터도착일자 시작 — 압축형 `yyyyMMdd`.
   * BE 가 `UI_RETURN_INVOICE.CLS_DATE`(varchar(8)) 문자열과 그대로 비교한다. 대시 형식을 보내면
   * 문자열 대소가 어긋나 결과가 조용히 빈다(`'20260722' > '2026-07-22'`).
   */
  clsDateFrom: string
  /** 센터도착일자 종료 — 압축형 `yyyyMMdd`. 형식 근거는 [clsDateFrom] 과 같다. */
  clsDateTo: string
  /** 반송번호 다건 */
  rtnInvoiceNos: string[]
  /**
   * 입고센터(UI_RETURN_INVOICE.WH_NM) 단건 EQUAL 필터. BE `ReturnReceiptController` 가 `centerNm` 으로 받는다.
   * RGR14 센터도착 수령작업 이력 조회(LOGISPRD-12610)와 RGR09 상세 재조회(고른 요약 행의 입고센터, LOGISPRD-14913)가 채운다. RGR09 요약 조회는 쓰지 않는다.
   */
  centerNm?: string
  /**
   * 지정일자(UI_RETURN_INVOICE.ASSIGN_DATE) 단건 EQUAL 필터 — 상세 조회만 받는다(BE #4603). RGR09 가 고른 요약 행의 지정일자로 상세를 좁힌다(LOGISPRD-14913).
   * 비면 싣지 않고 BE 도 조건을 걸지 않는다(1.0 RGR09 더블클릭·상세 엑셀과 같다).
   */
  assignDate?: string
}

/** 상세표 탭 — BE `tabIdx` 와 같은 순서다. */
export const RECEIPT_TABS = [
  { key: 'all', label: '전체 내역' },
  { key: 'success', label: '성공 내역' },
  { key: 'miss', label: '오입고 내역' },
  { key: 'unreg', label: '미등록 내역' },
  { key: 'dup', label: '중복스캔 내역' },
] as const

export type ReceiptTabKey = (typeof RECEIPT_TABS)[number]['key']

/**
 * `scanStatus` 는 한글 문자열로 온다(BE `ReturnReceiptJpaRepository` 상수).
 * 탭 키로 환산해 두면 상세표를 클라이언트에서 탭별로 거를 수 있다.
 */
export const SCAN_STATUS_TO_TAB: Record<string, ReceiptTabKey> = {
  성공: 'success',
  오입고: 'miss',
  미등록: 'unreg',
  중복스캔: 'dup',
}

/** 엑셀 공통 — 사유·파일 비밀번호. BE 가 검증하고 감사로그(`<사유코드>_<상세사유>`)를 남긴다. */
export interface ReturnReceiptExcelReason {
  reasonCode: string
  detailedReason: string
  password: string
}

/** 엑셀 범위(요약) — `POST /api/v1/return/receipts/summary/excel`. 조회와 같은 조건(yyyyMMdd). */
export interface ReturnReceiptSummaryExcelScope {
  clsDateFrom: string
  clsDateTo: string
  rtnInvoiceNo: string[]
}

/** 엑셀 범위(상세) — `POST /api/v1/return/receipts/excel`. 고른 요약 행의 원문 키 + 상태 탭. null 키는 BE 가 조건을 걸지 않는다. */
export interface ReturnReceiptItemExcelScope {
  /** `RECEIPT_TABS` 순서(0 전체·1 성공·2 오입고·3 미등록·4 중복스캔). */
  tabIdx: number
  clsDate: string | null
  centerNm: string | null
  assignDate: string | null
  rtnInvoiceNo: string[]
}

/** `GET /api/v1/return/receipts/summary` 응답 행. */
export interface ReturnReceiptSummaryRes {
  clsDate: string | null
  whNm: string | null
  assignDate: string | null
  totalCount: number
  successCount: number
  missCount: number
  failCount: number
  dupCount: number
}

/**
 * `GET /api/v1/return/receipts` 응답 행 — 화면이 쓰지 않는 필드는 선언하지 않는다.
 * 생성·수정 4종은 RGR09 조회 표에는 없지만 RGR14 수령작업 표가 쓴다(LOGISPRD-12610).
 */
export interface ReturnReceiptItemRes {
  sprNm: string | null
  clsDate: string | null
  scanDate: string | null
  rtnInvoiceNo: string | null
  whNm: string | null
  /**
   * 항상 값이 온다. 조회 where 절 첫 항이 `SCAN_STATUS <> 'INIT'` 인데(moms-be
   * `ReturnReceiptJpaRepository.commonPredicate`) SQL 3값 논리에서 `NULL <> 'INIT'` 은 참이 아니라
   * NULL 이라, SCAN_STATUS 가 NULL 인 행은 결과에 못 든다. 컬럼 자체는 nullable 이다(varchar(20) NULL).
   */
  scanStatus: string
  orgInvoiceNo: string | null
  giWhNm: string | null
  assignDate: string | null
  ordNo: string | null
  insDate: string | null
  insBy: string | null
  updDate: string | null
  updBy: string | null
}

/** 머리표 행. */
export interface ReturnReceiptSummary {
  closeDate: string
  inCenter: string
  assignDate: string
  totalQty: number
  normalQty: number
  missQty: number
  unregQty: number
  dupQty: number
}

/** 상세표 행. */
export interface ReturnReceiptItem {
  shipper: string
  assignDate: string
  closeDate: string
  arriveDate: string
  returnInvoiceNo: string
  inCenter: string
  /** 실입고 결과내역 — 원문 그대로 표시한다. */
  result: string
  /** 탭 필터용 환산 키. 알 수 없는 상태는 'all' 로 두어 전체 탭에만 남는다. */
  resultTab: ReceiptTabKey
  outCenter: string
  orgInvoiceNo: string
  orderNo: string
  worker: string
}
